import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ImageUploadService } from 'app/shared/services/image-upload/image-upload.service';
import { ApplicationService } from 'app/shared/services/app/application/application.service';
import { Application } from 'app/shared/services/app/application/application.model';
import { Session } from 'app/shared/services/session/session.service';
import { Toast } from 'app/shared/components/toast/toast.service';
import { ContentItem, TextContentItem, VideoContentItem, ImageContentItem,
         DividerContentItem, DocumentContentItem } from 'app/shared/services/app/content/models';
import { DragulaService } from 'ng2-dragula/ng2-dragula';
import { LoaderButton } from 'app/shared/components/loader-button/loader-button.component';
import { ActiveUser } from 'app/shared/services/user/active-user.model';
import { GlobalRole } from 'app/shared/services/user/base-user.model';
import { ModalService } from '../../services/modal/modal.service';
import { ContentEditorImageModalComponent } from './content-editor-image-modal/content-editor-image-modal.component';
import { ImageService } from 'app/shared/services/image/image.service';
import { ContentEditorDocumentModalComponent } from './content-editor-document-modal/content-editor-document-modal.component';
import { RichTextEditorComponent } from '../rich-text-editor/rich-text-editor.component';

@Component({
  selector: 'tog-content-editor',
  templateUrl: './content-editor.component.html',
  styleUrls: ['./content-editor.component.scss'],
  providers: [ImageService, ImageUploadService, DragulaService]
})
export class ContentEditorComponent implements OnInit, OnDestroy {
  @Input() content: ContentItem[] = [];
  @Input() primaryColor: string;

  user: ActiveUser;
  app: string;
  subscriptions: any[] = [];

  textForm: FormGroup;
  contentItemToEdit: ContentItem;
  contentItemToDelete: ContentItem;
  text: string = '';
  isCodeView: boolean = false;

  // Image modal
  initialImage: string = '';
  image: any;

  // Video modal
  initialVideoTitle: string = '';
  error: boolean = false;
  progressBarPercentage: number = 0;
  progressBarInterval: any;
  uploadType: string = '';
  youtubeEmbed: string = '';
  videoTitle: string = '';
  video: any;
  thumbnail: any;
  resetForm: EventEmitter<any> = new EventEmitter();
  contentChanged: boolean = false;
  reset: string;

  // Developer
  contentJSON: any[];

  @Output() contentUpdated: EventEmitter<ContentItem[]> = new EventEmitter();
  @Output() modalShown: EventEmitter<any> = new EventEmitter();

  @ViewChild('imageEditor') imageEditor: ContentEditorImageModalComponent;
  @ViewChild('documentEditor') documentEditor: ContentEditorDocumentModalComponent;
  @ViewChild('textEditor') textEditor: RichTextEditorComponent;
  @ViewChild('saveVideoButton') saveVideoButton: LoaderButton;
  @ViewChild('confirmDeleteContentButton') confirmDeleteContentButton: LoaderButton;

  constructor(private _ds: DragulaService,
              private _el: ElementRef,
              private _fb: FormBuilder,
              private _session: Session,
              private _ius: ImageUploadService,
              private _as: ApplicationService,
              public modal: ModalService,
              private _toast: Toast) {
    this.textForm = this._fb.group({
      title: [''],
      text: ['', Validators.required]
    });

    this._session.user().then((user: ActiveUser) => {
      this.user = user;
      return this._as.getApplicationDetails(this._session.selectedAppSysName);
    })
    .then((app: Application) => {
      this.app = app.sys_name;
    });
  }

  ngOnInit() {
    this._ds.setOptions('content', {
      moves: (el: Element, target: Element, source: Element, sibling: Element): boolean => {
        return !el.classList.contains('no-drag');
      },
      accepts: (el: Element, target: Element, source: Element, sibling: Element): boolean => {
        return !el.contains(target);
      }
    });
    this.subscriptions.push(this._ds.drop.subscribe(() => {
      setTimeout(() => {
        this.contentUpdated.emit(this.content);
      }, 20);
    }));
  }

  codeViewChanged(e) { this.isCodeView = e; }

  editContentItem(item: ContentItem) {
    this.contentItemToEdit = item;
    switch (item.type) {
      case 'text' :
        this.launchAddText(<TextContentItem>item);
        break;
      case 'pdf' :
      case 'spreadsheet' :
        this.launchAddDocument(<DocumentContentItem>item);
        break;
      case 'video' :
        this.launchAddVideo(<VideoContentItem>item);
        break;
      case 'image' :
        this.launchAddImage(<ImageContentItem>item);
        break;
    }
  }

  launchAddImage(imageItem: ImageContentItem = null) {
    this.imageEditor.show(imageItem);
  }

  launchAddDocument(documentItem: DocumentContentItem) {
    this.documentEditor.show(documentItem);
  }

  addDivider() {
    let divider = new DividerContentItem();
    this.content.push(divider);
    this.contentUpdated.emit(this.content);
  }

  launchAddText(textItem?: TextContentItem) {
    this.textForm.reset();
    if (textItem) {
      if (textItem.title) this.textForm.controls['title'].setValue(textItem.title);
      this.textForm.controls['text'].setValue(textItem.text);
      this.text = textItem.text;
    } else {
      this.text = '';
      this.contentItemToEdit = null;
    }
    this.modal.show('add-text-modal');
    this.modalShown.emit(true);
  }

  saveText() {
    let content = this.textForm.value;
    if (this.isCodeView) content.text = this.textEditor.codeViewText();
    if (this.contentItemToEdit) {
      let textItem = <TextContentItem>this.contentItemToEdit;
      textItem.title = content.title;
      textItem.text = content.text;
    } else {
      this.content.push(new TextContentItem(content));
    }
    this.contentUpdated.emit(this.content);
    this.modalShown.emit(false);
    this.text = ' ';
  }

  deleteContentItem(item: ContentItem) {
    this.contentItemToDelete = item;
    if (item.type === 'divider') this.confirmDeleteContentItem();
    else this.modal.show('delete-content-modal');
  }

  confirmDeleteContentItem() {
    let index = this.content.indexOf(this.contentItemToDelete);
    if (index > -1 ) this.content.splice(index, 1);
    this.contentUpdated.emit(this.content);
    this.confirmDeleteContentButton.setConfirmed();
  }

  onImageChanged(e) {
    if (e.isNew) {
      this.content.push(e.image);
    }
    this.contentUpdated.emit(this.content);
  }

  onDocumentChanged(e) {
    if (e.isNew) {
      this.content.push(e.document);
    }
    this.contentUpdated.emit(this.content);
  }

  readFile(file) {
    let self = this;
    let fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onloadend = function(e: any) {
      let image = new Image();
      image.src = e.target.result;

      image.onerror = () => {
        self._toast.error('Invalid file type. Allowed types: .png, .gif, .jpg, .jpeg');
      };
      image.onload = function() {
        self.onThumbnailChanged(image.src);
      };
    };
  }

  launchAddVideo(video?: VideoContentItem) {
    this.saveVideoButton.setInactive();
    // this.resetForm.emit('content-video');
    this.reset = 'content-video';
    // this.resetForm.emit('content-thumbnail');
    this.reset = 'content-thumbnail';
    this.initialVideoTitle = '';
    this.progressBarPercentage = 0;
    this.youtubeEmbed = '';
    this.video = null;
    this.thumbnail = null;
    this.videoTitle = '';
    this.uploadType = '';
    setTimeout((self) => {
      if (video) self.setVideoModalToVideoContentItem(video);
      else this.contentItemToEdit = null;
      this.error = false;
      this.modal.show('add-video-modal');
    }, 0, this);
    this.modalShown.emit(true);
  }

  setVideoModalToVideoContentItem(video: VideoContentItem) {
    this.videoTitle = video.title;
    if (video.embedLink) {
      this.youtubeEmbed = video.embedLink;
      this.uploadType = 'youtube';
    } else {
      this.uploadType = 'file';
      this.thumbnail = video.thumbnail;
      this.initialVideoTitle = video.videoString;
    }
  }

  isSaveVideoButtonDisabled() {
    if (this.error) return true;
    if (this.contentItemToEdit) {
      let video = <VideoContentItem>this.contentItemToEdit;
      if (this.uploadType === 'youtube') return (this.videoTitle === video.title && !this.isYoutubeEmbedValid());
      else return (!this.video && !this.thumbnail && this.videoTitle === video.title);
    } else {
      if (this.uploadType === 'youtube') return (!this.isYoutubeEmbedValid());
      else return (!this.thumbnail || !this.video);
    }
  }

  onVideoChanged(e: any) {
    this.error = false;
    this.video = e.file;
  }

  onThumbnailChanged(e: any) {
    this.thumbnail = e;
  }

  isYoutubeEmbedValid() {
    let embed = this.youtubeEmbed.trim();
    return embed.startsWith('<iframe width="560" height="315" src="https://www.youtube.com/embed/')
      && embed.endsWith('</iframe>');
  }

  saveVideo() {
    if (this.contentItemToEdit) this.saveEditedVideo();
    else if (this.uploadType === 'file') this.uploadVideoAndThumbnail();
    else {
      let video = new VideoContentItem({
        title: this.videoTitle,
        embed_link: this.youtubeEmbed
      });
      this.content.push(video);
      this.error = false;
      this.modal.hide();
    }
    this.contentUpdated.emit(this.content);
    this.modalShown.emit(false);
  }

  saveEditedVideo() {
    let video = <VideoContentItem>this.contentItemToEdit;
    video.title = this.videoTitle;

    if (this.uploadType === 'youtube') {
      video.embedLink = this.youtubeEmbed;
      this.error = false;
      this.saveVideoButton.setConfirmed();
    } else {
      if (this.video || this.thumbnail) this.uploadEditedVideoAndOrThumbnail(video);
      else this.saveVideoButton.setConfirmed();
    }
    this.contentUpdated.emit(this.content);
    this.modalShown.emit(false);
  }

  uploadEditedVideoAndOrThumbnail(video: VideoContentItem) {
    let app = this._session.selectedAppSysName;

    if (this.video) {
      this.progressBarInterval = setInterval(self => {
        self.progressBarPercentage = Math.min(75, self.progressBarPercentage + 25);
      }, 1000, this);

      this._ius.upload(app, this.video)
      .then((url) => {
        video.video = url;
        if (this.thumbnail) video.thumbnail = this.thumbnail;
        clearInterval(this.progressBarInterval);
        this.contentUpdated.emit(this.content);
        this.progressBarPercentage = 100;

        // So we can see the progress bar fill to completion
        setTimeout((self) => {
          self.saveVideoButton.setConfirmed();
        }, 200, this);
      });
    } else if (this.thumbnail) {
        video.thumbnail = this.thumbnail;
        this.saveVideoButton.setConfirmed();
    }
  }

  uploadVideoAndThumbnail() {
    let thumbnailUrl, videoUrl, app = this._session.selectedAppSysName;

    this.progressBarInterval = setInterval(self => {
      self.progressBarPercentage = Math.min(75, self.progressBarPercentage + 25);
    }, 1000, this);

    this._ius.upload(app, this.video)
    .then((url) => {
      videoUrl = url;
      thumbnailUrl = this.thumbnail;
      let video = new VideoContentItem({
        title: this.videoTitle,
        thumbnail: thumbnailUrl,
        video: videoUrl
      });
      clearInterval(this.progressBarInterval);
      this.progressBarPercentage = 100;
      this.content.push(video);
      this.contentUpdated.emit(this.content);

      // So we can see the progress bar fill to completion
      setTimeout((self) => {
        self.saveVideoButton.setConfirmed();
      }, 200, this);
    })
    .catch(() => {
      clearInterval(this.progressBarInterval);
      this._toast.error('Error uploading video.');
      this.saveVideoButton.setInactive();
    });
  }

  maxFileSizeExceeded() {
    this._toast.error('Image is too large. Please select an image less than 1MB.');
  }

  wrongFile(e: Event) {
    setTimeout(() => {
      this.error = true;
    }, 0);
  }

  // Developer shortcut for showing content JSON, for debugging purposes
  @HostListener('document:keydown.shift.arrowdown')
  showJSONContent() {
    if (this.user.globalRole.level >= GlobalRole.Admin) {
      this.contentJSON = this.content.map(contentItem => contentItem.toJson());
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/components/content-editor/content-editor.component.ts